import { Box, Divider, SelectChangeEvent } from "@mui/material";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown.tsx";
import { APPS } from "../../../../../constants/AppsConstants";
import LinkConstants from "../../../../../constants/LinkConstants";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { ImageEditingViewMode } from "../../../../../models/image-to-image";
import iframeStore from "../../../../IframeNew/iframe-store";
import TextToImageAspectRatio from "../../../../Playground/TextToImage/Config/Advanced/AspectRatio/TextToImageAspectRatio.tsx";
import TextToImageSteps from "../../../../Playground/TextToImage/Config/Advanced/Steps/TextToImageSteps.tsx";
import TextToImageAdvanced from "../../../../Playground/TextToImage/Config/Advanced/TextToImageAdvanced";
import ImageReference from "../../../../Playground/TextToImage/Config/ImageReference/ImageReference";
import TextToImageGenerateButton from "../../../../Playground/TextToImage/Generator/Search/Button/TextToImageGenerateButton";
import NewTgDropdown from "../../../Models/Dropdown/NewTgDropdown.tsx";
import { TgModel } from "../../../Models/store/tg-model-store.tsx";
import { isLightVersion } from "../../../utils/tg-utils.ts";
import TgPromptInput from "../../Video/Config/PromptInput/TgPromptInput";
import TgFastSwitch from "./Fast/TgFastSwitch.tsx";
import TgInfluenceSlider from "./Influence/TgInfluenceSlider";
import styles from "./TgImageGenerationConfig.module.scss";

export const tgAdvancedConfig = {
	steps_num: 8,
	max_results: 4,
	steps: {
		min: 4,
		max: 20,
	},
};

const TgImageGenerationConfig = () => {
	const { t } = useTranslation("tg", { keyPrefix: "app.image.config" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const { handleConfigChange } = textToImageStore;
	const disablePrompt = !textToImageStore.config.tailored_model_id || textToImageStore.config.restyle_portrait;
	const containerRef = useRef<HTMLDivElement | null>(null);
	const showFastSwitch = isLightVersion(textToImageStore.config.training_version);

	useEffect(() => {
		runInAction(() => {
			playgroundStore.handleAppChange(APPS.TG_IMAGE_GENERATION);
			playgroundStore.imageEditingViewMode = ImageEditingViewMode.GALLERY;
			textToImageStore.errors = {};
			textToImageStore.IpSignalWarning = false;
		});
	}, []);

	useEffect(() => {
		textToImageStore.imageGenerationContainerRef = containerRef;
	}, [containerRef]);

	const handleModelChange = (_e: SelectChangeEvent<string>, selectedItem: DropDownItem<string, TgModel>) => {
		const { key, value, extraData } = selectedItem;
		const { id, training_version, generation_prefix, active_model_version } = extraData || {};

		runInAction(() => {
			textToImageStore.config = {
				...textToImageStore.config,
				model: "",
				model_version: "",
				model_name: key,
				model_id: value,
				tailored_model_id: id?.toString(),
				checkpoint_number: active_model_version,
				training_version,
				generation_prefix,
				include_generation_prefix: false,
				negative_prompt: undefined,
				content_moderation: false,
				prompt_enhancement: false,
			};
		});
	};

	return (
		<Box className={styles.config}>
			<Box className={styles.inputs} ref={containerRef}>
				<InputLayout label={t("models.label")} docsLink={LinkConstants.TG.GENERATION.DOCS}>
					<NewTgDropdown
						value={textToImageStore.config.tailored_model_id as string}
						onChange={handleModelChange}
						alwaysRefresh
					/>
				</InputLayout>

				<TgInfluenceSlider />
				<TextToImageSteps />
				<TgFastSwitch hide={!showFastSwitch} />
				<TextToImageAspectRatio />

				<InputLayout label={t("prompt.label")}>
					<TgPromptInput
						prefix={
							textToImageStore.config.restyle_portrait ? "" : textToImageStore.config.generation_prefix
						}
						prompt={textToImageStore.config.prompt}
						placeholder={
							textToImageStore.config.restyle_portrait
								? t("prompt.portraitPlaceholder")
								: t("prompt.placeholder")
						}
						onChange={(newPrompt) => handleConfigChange("prompt", newPrompt)}
						onEnter={textToImageStore.generateTextToImage}
						disabled={disablePrompt}
						maxRows={8}
					/>
				</InputLayout>
				<Divider className={styles.divider} />
				{(!iframeStore.isIframe() ||
					iframeStore.iframe.config.image_generation_config?.enable_structure_guidance) && (
					<>
						<ImageReference disabledMessage={t("imageReference.disabledMessage")} />
						<Divider className={styles.divider} />
					</>
				)}

				<TextToImageAdvanced hideSteps />
			</Box>
			<TextToImageGenerateButton className={styles.primaryBtn} />
		</Box>
	);
};

export default observer(TgImageGenerationConfig);
